import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { LogoImage } from "./Logo";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "Solutions", href: "#solutions" },
  { label: "How to Setup", href: "#how-to-setup" },
  { label: "Workflows", href: "#workflows" },
  { label: "Integrations", href: "#integrations" },
  { label: "Pricing", href: "#pricing" },
  { label: "About", href: "#about" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setActive(en.target.id);
        });
      },
      { rootMargin: "-35% 0px -55% 0px" }
    );
    LINKS.forEach((l) => {
      const el = document.getElementById(l.href.slice(1));
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const linkClass = (href) =>
    `text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
      active === href.slice(1)
        ? "text-nova-blue"
        : "text-nova-slate hover:text-nova-navy"
    }`;

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-white border-b border-[#E8EEF7]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center justify-between py-2">
          <LogoImage />

          <nav data-testid="desktop-nav" className="hidden xl:flex items-center gap-7 ml-10">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={linkClass(l.href)}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-6 ml-10 shrink-0">
            <Link
              to="/login"
              data-testid="sign-in-link"
              className="text-sm font-medium text-nova-slate hover:text-nova-navy transition-colors duration-300"
            >
              Sign In
            </Link>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Link
                to="/request-demo"
                data-testid="nav-request-demo-btn"
                className="inline-flex items-center gap-2 rounded-full bg-nova-blue px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgb(37,99,235,0.35)] hover:bg-nova-bluedark transition-colors duration-300"
              >
                Request Demo
                <ArrowRight size={15} strokeWidth={2} />
              </Link>
            </motion.div>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-slate-200 text-nova-navy bg-white"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="xl:hidden overflow-hidden bg-white border-b border-slate-200"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="py-3 text-base font-medium text-nova-navy border-b border-slate-100"
                >
                  {l.label}
                </motion.a>
              ))}
              <div className="pt-5 flex items-center gap-4">
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-sign-in-link"
                  className="text-sm font-medium text-nova-slate"
                >
                  Sign In
                </Link>
                <Link
                  to="/request-demo"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-request-demo-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-nova-blue px-5 py-2.5 text-sm font-semibold text-white"
                >
                  Request Demo
                  <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
